import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/Button';
import ServicesSection from '@/components/ServicesSection';
import { useUser } from '@/utils/useUser';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import {
  SparklesIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
  LinkIcon,
  UserGroupIcon,
  CubeTransparentIcon,
  CheckIcon,
  ChatBubbleLeftEllipsisIcon,
  ArrowsRightLeftIcon
} from '@heroicons/react/24/outline';
import { getActiveProductsWithPrices } from '@/utils/supabase-client';
import { CheckBadgeIcon, StarIcon } from '@heroicons/react/24/solid';
import { SEOMeta } from '@/templates/SEOMeta';
import { generateFAQPageSchema, generateSoftwareApplicationSchema } from '@/utils/schema';
import { useTranslations } from 'next-intl';
import {getI18nProps} from "@/utils/i18n";
import IntegrationMethodsOrbit from '@/components/IntegrationMethodsOrbit';
import { motion, useReducedMotion } from "framer-motion";

const Pricing = dynamic(() => import('@/components/Pricing'));

export const getStaticProps = getI18nProps;

export default function Index() {
  const t = useTranslations('home');
  const { user } = useUser();
  const router = useRouter();
  const prefersReducedMotion = useReducedMotion();
  const [products, setProducts] = useState<
      Awaited<ReturnType<typeof getActiveProductsWithPrices>> | null
  >(null);

  type Persona = 'business' | 'creator';
  const [persona, setPersona] = useState<Persona>('business');

  const getProducts = async () => {
    const fetched = await getActiveProductsWithPrices();
    setProducts(fetched);
  };

  useEffect(() => {
    if (products === null) getProducts();
    if (user) router.replace('/dashboard');
  }, [user, products]);

  // Allow deep-linking (e.g. /?persona=creator)
  useEffect(() => {
    const q = router.query;
    const raw = (q.persona ?? q.type ?? q.for) as string | string[] | undefined;
    const val = Array.isArray(raw) ? raw[0] : raw;
    if (!val) return;
    const normalized = String(val).toLowerCase();
    if (['creator', 'creators', 'affiliate', 'affiliates'].includes(normalized)) setPersona('creator');
    if (['business', 'partner', 'partners', 'company', 'companies'].includes(normalized)) setPersona('business');
  }, [router.query]);

  const isBusiness = persona === 'business';

  const businessFeatures = useMemo(
      () => [
        {
          id: 'tracking',
          title: t('services.items.tracking.title'),
          icon: ChartBarIcon,
          description: t('services.items.tracking.description')
        },
        {
          id: 'payouts',
          title: t('services.items.payouts.title'),
          icon: CurrencyDollarIcon,
          description: t('services.items.payouts.description')
        },
        {
          id: 'campaignControl',
          title: t('services.items.campaignControl.title'),
          icon: UserGroupIcon,
          description: t('services.items.campaignControl.description')
        },
        {
          id: 'integrations',
          title: t('services.items.integrations.title'),
          icon: CubeTransparentIcon,
          description: t('services.items.integrations.description')
        },
        {
          id: 'analytics',
          title: t('services.items.analytics.title'),
          icon: ChatBubbleLeftEllipsisIcon,
          description: t('services.items.analytics.description')
        },
        {
          id: 'database',
          title: t('services.items.database.title'),
          icon: LinkIcon,
          description: t('services.items.database.description')
        }
      ],
      [t]
  );

  const creatorFeatures = useMemo(
      () => [
        {
          id: 'tracking',
          title: t('services.items.tracking.title'),
          icon: ChartBarIcon,
          description: t('services.items.tracking.description')
        },
        {
          id: 'analytics',
          title: t('services.items.analytics.title'),
          icon: ChatBubbleLeftEllipsisIcon,
          description: t('services.items.analytics.description')
        },
        {
          id: 'payouts',
          title: t('services.items.payouts.title'),
          icon: CurrencyDollarIcon,
          description: t('services.items.payouts.description')
        }
      ],
      [t]
  );

  const features = isBusiness ? businessFeatures : creatorFeatures;

  const valueProps = useMemo(() => {
    const common = [
      {
        id: 'trial',
        title: t('valueProps.trial.title'),
        description: t('valueProps.trial.description'),
        icon: <CheckIcon className="w-12 h-12 mx-auto text-green-600" />
      },
      {
        id: 'zeroCommission',
        title: t('valueProps.zeroCommission.title'),
        description: t('valueProps.zeroCommission.description'),
        icon: <CheckIcon className="w-12 h-12 mx-auto text-blue-600" />
      }
    ];

    if (isBusiness) {
      return [
        {
          id: 'businesses',
          title: t('valueProps.businesses.title'),
          description: t('valueProps.businesses.description'),
          icon: <CubeTransparentIcon className="w-12 h-12 mx-auto text-blue-600" />
        },
        ...common
      ];
    }

    return [
      {
        id: 'creators',
        title: t('valueProps.creators.title'),
        description: t('valueProps.creators.description'),
        icon: <SparklesIcon className="w-12 h-12 mx-auto text-purple-600" />
      },
      ...common
    ];
  }, [isBusiness, t]);

  const businessBullets = ['commissions', 'rules', 'integrations', 'gdpr'] as const;

  const creatorBullets = ['realtime', 'payouts', 'transparentStatus', 'noFees'] as const;

  const howItWorksSteps = persona === 'creator'
      ? (['profile', 'join', 'getPaid'] as const)
      : (['connect', 'create', 'payout'] as const);

  const howItWorksIcons: Record<string, any> = {
    connect: CubeTransparentIcon,
    create: SparklesIcon,
    payout: CurrencyDollarIcon,
    profile: UserGroupIcon,
    join: LinkIcon,
    getPaid: CurrencyDollarIcon
  };

  const heroImageSrc = isBusiness ? '/dashboard_home_cropped.webp' : '/forcreator.webp';
  const heroImageAlt = isBusiness ? t('images.heroAlt') : t('images.creatorHeroAlt');
  const heroImageWidth = isBusiness ? 2212 : 1623;
  const heroImageHeight = isBusiness ? 1229 : 787;
  const heroBadgeKey = isBusiness ? 'business' : 'creator';
  const personaOptions = [
    {
      id: 'business' as Persona,
      label: t('hero.tabs.business'),
      Icon: ChartBarIcon
    },
    {
      id: 'creator' as Persona,
      label: t('hero.tabs.creator'),
      Icon: SparklesIcon
    }
  ];

  return (
      <>
        <SEOMeta
            title={t('seo.title')}
            description={t('seo.description')}
            keywords={t('seo.keywords')}
            url="/"
            structuredData={[
              generateSoftwareApplicationSchema({
                description: t('seo.description'),
                url: 'https://afivio.com/'
              }),
              generateFAQPageSchema([
                {
                  question: 'What is Afivio affiliate marketing software used for?',
                  answer: 'Afivio helps teams run affiliate and referral programs in one place. You can track results, manage commissions, and review payout status.'
                },
                {
                  question: 'Can Afivio run both affiliate and referral programs?',
                  answer: 'Yes. You can run both models at the same time and keep separate rules, rewards, and partner access.'
                },
                {
                  question: 'Does Afivio support creator and business workflows?',
                  answer: 'Yes. Businesses get campaign and payout controls, while creators get link, performance, and payout tracking.'
                },
                {
                  question: 'How quickly can a partner program be launched with Afivio?',
                  answer: 'Teams can launch by connecting their store, adding commission rules, and inviting partners.'
                }
              ])
            ]}
        />

        <div className="page-wrapper">
          {/* Hero Section */}
          <section className="section home-hero bg-gradient-to-br from-blue-50 to-indigo-50">
            <div className="home-hero-area">
              <div className="max-w-7xl mx-auto px-6">
                <div className="home-hero-component">
                  <div className="home-hero-top">
                    <div className="max-w-4xl mx-auto">
                      <div className="home-hero-content-wrap">
                        {/* Persona Switch */}
                        <div className="flex justify-center pt-10 px-4">
                          <div className="w-full max-w-lg rounded-[1.75rem] border border-slate-200 bg-white/90 p-1.5 shadow-lg shadow-slate-900/10 ring-1 ring-white/80 backdrop-blur">
                            <div className="grid grid-cols-2 gap-1.5">
                              {personaOptions.map((option) => {
                                const active = persona === option.id;
                                const Icon = option.Icon;
                                return (
                                  <motion.button
                                    key={option.id}
                                    type="button"
                                    aria-pressed={active}
                                    onClick={() => setPersona(option.id)}
                                    whileHover={{ y: -1 }}
                                    whileTap={{ scale: 0.98 }}
                                    transition={{ type: 'spring', stiffness: 420, damping: 28 }}
                                    className={`group flex items-center justify-center gap-2.5 rounded-[1.35rem] px-3 py-2.5 text-sm font-semibold transition-colors duration-200 ${
                                      active
                                        ? 'bg-slate-200 text-slate-800 shadow-sm shadow-slate-900/10'
                                        : 'text-slate-400 hover:bg-slate-100 hover:text-slate-700'
                                    }`}
                                  >
                                    <motion.span
                                      layout
                                      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-2xl transition-colors duration-200 ${
                                        active
                                          ? 'bg-white/70 text-slate-700'
                                          : 'bg-slate-100 text-slate-400 group-hover:bg-slate-200 group-hover:text-slate-600'
                                      }`}
                                    >
                                      <Icon className="h-4 w-4" />
                                    </motion.span>
                                    <span>{option.label}</span>
                                  </motion.button>
                                );
                              })}
                            </div>
                          </div>
                        </div>

                        <div className="home-hero-badge mt-8 px-4 text-center">
                          <p className="paragraph-regular m-0 text-center text-lg text-gray-600">
                            {t(`hero.${heroBadgeKey}.badgeInline.prefix`) && (
                              <>{t(`hero.${heroBadgeKey}.badgeInline.prefix`)} </>
                            )}
                            <span className="rounded bg-blue-100/80 px-1.5 py-0.5 font-semibold text-blue-700">
                              {t(`hero.${heroBadgeKey}.badgeInline.highlight`)}
                            </span>
                            {t(`hero.${heroBadgeKey}.badgeInline.suffix`) && (
                              <> {t(`hero.${heroBadgeKey}.badgeInline.suffix`)}</>
                            )}
                          </p>
                        </div>

                          <h1 className="py-6 hero-heading text-5xl lg:text-6xl font-bold text-gray-900 text-center px-4">
                              <span className="flex flex-col items-center leading-tight max-w-[calc(100vw-2rem)]">
                                <span className="whitespace-normal sm:whitespace-nowrap break-words [overflow-wrap:anywhere]">
                                  {isBusiness ? t('hero.business.title') : t('hero.creator.title')}
                                </span>

                                <span className="whitespace-normal sm:whitespace-nowrap break-words [overflow-wrap:anywhere] text-blue-600">
                                  {isBusiness ? t('hero.business.titleHighlight') : t('hero.creator.titleHighlight')}
                                </span>
                              </span>
                          </h1>

                        <div className="mb-5">
                          <div className="max-w-2xl mx-auto">
                            <p className="paragraph-regular text-gray-600 text-center text-lg">
                              {isBusiness ? t('hero.business.subtitle') : t('hero.creator.subtitle')}
                            </p>
                          </div>
                        </div>

                        <div className="hero-button-block flex justify-center">
                          <div className="relative isolate">
                            <motion.span
                              aria-hidden="true"
                              className="absolute -inset-4 -z-10 rounded-[1.5rem] bg-gradient-to-r from-cyan-400 via-blue-500 to-violet-500 opacity-50 blur-2xl"
                              animate={prefersReducedMotion ? { scale: 1, opacity: 0.5 } : { scale: [0.94, 1.12, 0.94], opacity: [0.35, 0.75, 0.35] }}
                              transition={{ duration: 2.4, repeat: prefersReducedMotion ? 0 : Infinity, ease: 'easeInOut' }}
                            />
                            <div className="relative overflow-hidden rounded-2xl bg-blue-300/80 p-[2px] shadow-[0_18px_38px_-16px_rgba(37,99,235,0.75)]">
                              <motion.span
                                aria-hidden="true"
                                className="absolute -inset-[80%] bg-[conic-gradient(from_90deg_at_50%_50%,transparent_0deg,transparent_40deg,#ffffff_58deg,#bae6fd_70deg,transparent_82deg,transparent_170deg,#ffffff_188deg,#bfdbfe_200deg,transparent_215deg,transparent_300deg,#ffffffcc_318deg,#dbeafe_330deg,transparent_342deg,transparent_360deg)]"
                                animate={{ rotate: prefersReducedMotion ? 0 : 360 }}
                                transition={{ duration: 3.6, repeat: prefersReducedMotion ? 0 : Infinity, ease: 'linear' }}
                              />
                              <Button
                                  blue
                                  onClick={() =>
                                      isBusiness
                                          ? router.push('/signup?type=business')
                                          : router.push(`${process.env.NEXT_PUBLIC_AFFILIATE_SITE_URL}/signup`)
                                  }
                                  className="group relative !rounded-[0.9rem] !bg-blue-600 px-9 py-4 text-base shadow-none ring-1 ring-inset ring-white/30 hover:!scale-[1.02] hover:!bg-blue-700"
                              >
                                <span aria-hidden="true" className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/25 to-transparent transition-transform duration-700 group-hover:translate-x-full" />
                                <span className="relative z-10">
                                  {isBusiness ? t('hero.business.cta1') : t('hero.creator.cta1')}
                                </span>
                              </Button>
                            </div>
                          </div>
                        </div>
                        <div className="text-center mt-2">
                          <p className="flex items-center justify-center text-gray-500 text-sm font-medium">
                            <CheckBadgeIcon className="w-5 h-5 text-green-500 mr-2" />
                            {isBusiness ? t('hero.business.cta2') : t('hero.creator.cta2')}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="home-hero-bottom relative left-1/2 mt-[clamp(2.5rem,5vw,6rem)] w-screen -translate-x-1/2">
                    <div className="hero-image-wrap w-full px-[10vw]">
                      <Image
                        src={heroImageSrc}
                        alt={heroImageAlt}
                        className="home-hero-image mx-auto h-auto w-full rounded-2xl drop-shadow-[0_1em_3em_rgba(2,6,23,0.5)]"
                        width={heroImageWidth}
                        height={heroImageHeight}
                        priority
                        sizes="80vw"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Features Section */}
          <ServicesSection
              features={features}
              title={isBusiness ? t('servicesHeader.business.title') : t('servicesHeader.creator.title')}
              subtitle={isBusiness ? t('servicesHeader.business.subtitle') : t('servicesHeader.creator.subtitle')}
          />

          {/* How it works */}
          <section id="how-it-works" className="py-20 bg-white">
            <div className="max-w-7xl mx-auto px-6">
              <div className="text-center mb-12">
                <h2 className="text-4xl font-bold mb-4">{t('howItWorks.title')}</h2>
                <p className="text-gray-600 max-w-2xl mx-auto">{t('howItWorks.subtitle')}</p>

                <div className="mt-8 flex justify-center px-4">
                  <div className="w-full max-w-lg rounded-[1.75rem] border border-gray-200 bg-gray-50 p-1.5 shadow-sm">
                    <div className="grid grid-cols-2 gap-1.5">
                      {personaOptions.map((option) => {
                        const active = persona === option.id;
                        const Icon = option.Icon;
                        return (
                          <button
                            key={option.id}
                            type="button"
                            aria-pressed={active}
                            onClick={() => setPersona(option.id)}
                            className={`flex items-center justify-center gap-2 rounded-[1.35rem] px-3 py-3 text-sm font-semibold transition ${
                              active
                                ? 'bg-white text-gray-950 shadow-sm ring-1 ring-gray-200'
                                : 'text-gray-600 hover:bg-white/70 hover:text-gray-900'
                            }`}
                          >
                            <Icon className={`h-4 w-4 ${active ? 'text-blue-600' : 'text-gray-400'}`} />
                            {option.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-8">
                {howItWorksSteps.map((stepKey, idx) => {
                  const Icon = howItWorksIcons[stepKey];
                  const base = persona === 'creator' ? 'howItWorks.creator' : 'howItWorks.business';
                  return (
                      <div
                        key={stepKey}
                        className="bg-gray-50 p-8 rounded-2xl border border-gray-100 relative"
                      >
                        {/* Іконка в правому верхньому куті */}
                        <div className="absolute top-4 right-4 w-12 h-12 rounded-xl bg-white shadow-sm flex items-center justify-center">
                          <Icon className="w-6 h-6 text-blue-600" />
                        </div>

                        {/* Контент (без іконки зліва) */}
                        <div className="mt-6 text-sm font-semibold text-blue-600">{t('howItWorks.stepLabel', { number: idx + 1 })}</div>
                        <h3 className="text-xl font-bold mt-2">{t(`${base}.steps.${stepKey}.title`)}</h3>
                        <p className="text-gray-600 mt-3">{t(`${base}.steps.${stepKey}.description`)}</p>
                      </div>
                  );
                })}
              </div>
            </div>
          </section>


          {/* Value Proposition */}
          <section className="py-16 bg-gradient-to-r from-blue-50 to-green-50">
            <div className="max-w-7xl mx-auto px-6">
              <div className={`grid gap-8 text-center ${valueProps.length === 3 ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
                {valueProps.map((item, idx) => {
                  const isTrialItem = item.id === 'trial';

                  let translationKey = item.id;
                  if (isTrialItem && persona === 'creator') {
                    translationKey = 'creatorTrial';
                  }

                  return (
                      <div
                        key={item.id}
                        className="bg-white p-8 rounded-2xl shadow-md"
                      >
                        {item.icon}
                        <h3 className="text-2xl font-bold mt-6 mb-3">{t(`valueProps.${translationKey}.title`)}</h3>
                        <p className="text-gray-600">{t(`valueProps.${translationKey}.description`)}</p>
                      </div>
                  );
                })}
              </div>
            </div>
          </section>



          {/* Social Proof */}
          <section className="py-24 bg-white">
            <div className="max-w-7xl mx-auto px-6">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold mb-4">{t('socialProof.title')}</h2>
                <p className="text-gray-600 max-w-xl mx-auto">
                  {t('socialProof.subtitle')}
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {[
                  {
                    name: "Iryna Furman",
                    role: "Head of Digital Marketing, UAMASTER",
                    text: "\"It's clear that Afivio genuinely cares about customer success, and we truly appreciate that.\"",
                    summary: "The review highlights hands-on referral program setup, helpful campaign recommendations, and fast responses from the Afivio team.",
                    link: "https://clutch.co/profile/afivio#reviews"
                  },
                  {
                    name: "Anna Netakhata",
                    role: "Partnership Development Manager, Inweb",
                    text: "\"What stood out most about Afivio was their technical expertise combined with a client-focused approach.\"",
                    summary: "The review points to strong technical expertise, clear cooperation, and a practical approach to affiliate marketing implementation.",
                    link: "https://clutch.co/profile/afivio#reviews"
                  },
                  {
                    name: "Maya Nowak",
                    role: "Affiliate Creator",
                    text: "\"The creator dashboard feels clean and focused. I can quickly see which links perform, what is pending, and where payouts stand without digging through menus.\"",
                    summary: "The UX keeps affiliate data readable: campaign links, results, and payout status are easy to scan in one place.",
                    link: undefined
                  }
                ].map((testimonial, idx) => (
                    <motion.div
                        key={idx}
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-100px" }}
                        className="p-8 rounded-2xl bg-gradient-to-br from-gray-50 to-white border border-gray-100 hover:border-blue-100 transition-all"
                    >
                      <div className="mb-6 flex items-start justify-between gap-4">
                        <div>
                          <p className="font-semibold text-lg">{testimonial.name}</p>
                          <p className="text-sm text-gray-500">{testimonial.role}</p>
                        </div>
                        <div className="shrink-0 rounded-full border border-amber-100 bg-amber-50 px-3 py-1.5 shadow-sm">
                          <div className="flex items-center gap-1">
                            {[...Array(5)].map((_, i) => (
                                <StarIcon key={i} className="h-3.5 w-3.5 text-amber-400" />
                            ))}
                          </div>
                        </div>
                      </div>
                      <p className="text-gray-600">{testimonial.text}</p>
                      <p className="mt-4 text-sm text-gray-500">{testimonial.summary}</p>
                      {testimonial.link && (
                          <a
                              href={testimonial.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mt-6 inline-flex text-sm font-semibold text-blue-600 hover:text-blue-700"
                          >
                            Read the review on Clutch
                          </a>
                      )}
                    </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/*/!* Clutch Reviews *!/*/}
          {/*{isBusiness && (*/}
          {/*    <section className="py-12 sm:py-16 bg-[#fafafa] overflow-hidden">*/}
          {/*      <Script*/}
          {/*        id="clutch-widget-script"*/}
          {/*        src="https://widget.clutch.co/static/js/widget.js"*/}
          {/*        strategy="afterInteractive"*/}
          {/*        onReady={() => (window as any).CLUTCHCO?.Init?.()}*/}
          {/*      />*/}
          {/*      <div className="max-w-7xl mx-auto px-4 sm:px-6">*/}
          {/*        <div className="text-center mb-8 sm:mb-10">*/}
          {/*          <h2 className="text-2xl sm:text-4xl font-bold mb-4 max-w-3xl mx-auto leading-tight">*/}
          {/*            See what customers say about working with Afivio*/}
          {/*          </h2>*/}
          {/*        </div>*/}
          {/*        <div*/}
          {/*          className="clutch-widget clutch-widget-cropped"*/}
          {/*          data-url="https://widget.clutch.co"*/}
          {/*          data-widget-type="12"*/}
          {/*          data-height="375"*/}
          {/*          data-nofollow="false"*/}
          {/*          data-expandifr="true"*/}
          {/*          data-scale="100"*/}
          {/*          data-reviews="413497,413198"*/}
          {/*          data-clutchcompany-id="2571457"*/}
          {/*        />*/}
          {/*      </div>*/}
          {/*    </section>*/}
          {/*)}*/}

          {/* For Businesses (only) */}
          {isBusiness && (
              <section className="py-24 bg-white border-y border-gray-100" id="for-businesses">
                <div className="max-w-7xl mx-auto px-6">
                  <div className="grid lg:grid-cols-2 gap-16 items-center">
                    <div className="space-y-8">
                      <h2 className="text-4xl font-bold">{t('business.title')}</h2>
                      <p className="text-lg text-gray-600">{t('business.subtitle')}</p>

                      <ul className="space-y-6">
                        {businessBullets.map((key, idx) => (
                            <li key={key} className="flex items-start gap-4">
                              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mt-1 flex-shrink-0">
                                <CheckIcon className="w-5 h-5 text-blue-600" />
                              </div>
                              <span className="text-lg">{t(`business.bullets.${key}`)}</span>
                            </li>
                        ))}
                      </ul>

                      <div className="pt-4">
                        <Button blue onClick={() => router.push('/signup?type=business')}>
                          {t('business.cta')}
                        </Button>
                        <p className="text-sm text-gray-500 mt-3">{t('business.note')}</p>
                      </div>
                    </div>

                    <div className="relative">
                      <Image
                        src="/board.webp"
                        alt={t('images.businessAlt')}
                        className="rounded-2xl shadow-xl"
                        width={584}
                        height={388}
                        loading="lazy"
                        sizes="(max-width: 1024px) 100vw, 584px"
                      />
                      <div className="absolute -bottom-6 -right-6 bg-white p-4 rounded-xl shadow-lg transition-transform duration-200 hover:-translate-y-1">
                        <div className="flex items-center gap-2">
                          <div className="p-2 bg-green-100 rounded-lg">
                            <ChartBarIcon className="w-6 h-6 text-green-600" />
                          </div>
                          <div>
                            <p className="font-bold">{t('business.stat.title')}</p>
                            <p className="text-xs text-gray-500">{t('business.stat.subtitle')}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
          )}

          {/* For Creators (only) */}
          {!isBusiness && (
              <section className="py-24 bg-gradient-to-br from-blue-50 to-indigo-50" id="for-creators">
                <div className="max-w-7xl mx-auto px-6">
                  <div className="grid lg:grid-cols-2 gap-16 items-center">
                    <div className="relative order-last lg:order-first">
                      <Image
                        src="/forpeople.webp"
                        alt={t('images.creatorsAlt')}
                        className="rounded-2xl shadow-xl"
                        width={584}
                        height={388}
                        loading="lazy"
                        sizes="(max-width: 1024px) 100vw, 584px"
                      />
                      <div className="absolute -top-6 -left-6 bg-white p-4 rounded-xl shadow-lg transition-transform duration-200 hover:-translate-y-1">
                        <div className="flex items-center gap-2">
                          <div className="p-2 bg-purple-100 rounded-lg">
                            <CurrencyDollarIcon className="w-6 h-6 text-purple-600" />
                          </div>
                          <div>
                            <p className="font-bold">{t('creators.stat.title')}</p>
                            <p className="text-xs text-gray-500">{t('creators.stat.subtitle')}</p>
                          </div>
                        </div>
                      </div>

                    </div>

                    <div className="space-y-8">
                      <h2 className="text-4xl font-bold">{t('creators.title')}</h2>
                      <p className="text-lg text-gray-600">{t('creators.subtitle')}</p>

                      <ul className="space-y-6">
                        {creatorBullets.map((key) => (
                            <li key={key} className="flex items-start gap-4">
                              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mt-1 flex-shrink-0">
                                <CheckIcon className="w-5 h-5 text-green-600" />
                              </div>
                              <span className="text-lg">{t(`creators.bullets.${key}`)}</span>
                            </li>
                        ))}
                      </ul>

                      <div className="pt-4">
                        <Button
                            green
                            onClick={() =>
                                router.push(`${process.env.NEXT_PUBLIC_AFFILIATE_SITE_URL}/signup`)
                            }
                        >
                          {t('creators.cta')}
                        </Button>
                        <p className="text-sm text-gray-500 mt-3">{t('creators.note')}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
          )}

          {/* Pricing Section (business only) */}
          {isBusiness && (
              <section className="section py-24 bg-gray-50" id="pricing">
                <div className="max-w-7xl mx-auto px-6">
                  <div className="customer-component">
                    <div className="section-headear text-center mb-16">
                      <div className="max-w-2xl mx-auto">
                        <h2 className="section-heading text-4xl font-bold mb-6">
                          {t('pricing.title')}
                          <span className="text-blue-600"> {t('pricing.titleHighlight')}</span>
                          {t('pricing.titleAfter')}
                        </h2>

                        <p className="text-gray-600 mb-6">{t('pricing.subtitle')}</p>

                        <div className="flex flex-col items-center space-y-1">
                          <div className="inline-flex items-center bg-blue-50 rounded-full px-4 py-2">
                            <ArrowsRightLeftIcon className="w-5 h-5 text-blue-600 mr-2" />
                            <span>{t('pricing.pills.switchAnytime')}</span>
                          </div>

                          <div className="inline-flex items-center bg-gray-50 rounded-full px-4 py-2">
                            <CheckBadgeIcon className="w-5 h-5 text-green-500 mr-2" />
                            <span>{t('pricing.pills.noHiddenFees')}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="mt-12">
                      <Pricing products={products} />
                    </div>
                  </div>
                </div>
              </section>
          )}
          {isBusiness && (<IntegrationMethodsOrbit />)}

          {/* CTA Section */}
          <section className="section cta py-16 bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
            <div className="max-w-7xl mx-auto px-6">
              <div className="cta-component">
                <div className="w-layout-grid cta-grid grid md:grid-cols-2 gap-16 items-center">
                  <div className="cta-left">
                    <h2 className="cta-heading text-4xl font-bold mb-6">
                      {isBusiness ? t('cta.business.title') : t('cta.creator.title')}
                    </h2>
                    <div className="mt-4">
                      <p className="paragraph-regular text-white">
                        {isBusiness ? t('cta.business.subtitle') : t('cta.creator.subtitle')}
                      </p>
                    </div>
                    <div className="mt-8">
                      <Button
                          gray
                          onClick={() =>
                              isBusiness
                                  ? router.push('/signup?type=business')
                                  : router.push(`${process.env.NEXT_PUBLIC_AFFILIATE_SITE_URL}/signup`)
                          }
                          className="px-8 py-4"
                      >
                        {isBusiness ? t('cta.business.button') : t('cta.creator.button')}
                      </Button>
                    </div>
                  </div>

                  <div className="cta-right">
                    <Image
                      src="/dashboard.webp"
                      alt={isBusiness ? t('images.ctaAlt') : t('images.creatorsAlt')}
                      className="cta-image rounded-xl shadow-xl w-96 h-auto relative left-4"
                      width={384}
                      height={384}
                      loading="lazy"
                      sizes="(max-width: 768px) 80vw, 384px"
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </>
  );
}
